import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Save, Loader2, Info, Lock } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { cn } from "@/lib/cn";
import { ACSA_DEFAULTS, DEFAULT_ROUND, validateCoverage, profileFromMatter } from "@/lib/coverage";
import { useFunder } from "@/funder/FunderContext";
import { Button } from "@/components/ui-kit/Button";
import { Input, Label } from "@/components/ui-kit/Input";
import { Select } from "@/components/ui-kit/Select";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui-kit/Card";
import { CoverageEditor } from "@/components/matters/CoverageEditor";
import { CoveragePreview } from "@/components/matters/CoveragePreview";

const BLANK = {
  name: "",
  type: "ACSA_Client",
  hourly_rate: "300",
  status: "Open",
  require_time_review: false,
  open_date: "",
  close_date: "",
  notes: "",
};

export function MatterFormPage() {
  const { clientId: paramClientId, matterId } = useParams();
  const navigate = useNavigate();
  const { abbr, typeLabel } = useFunder();
  const isEdit = Boolean(matterId);

  const [loading, setLoading] = useState(isEdit);
  const [clientId, setClientId] = useState(paramClientId || null);
  const [issuedCount, setIssuedCount] = useState(0);
  const [form, setForm] = useState(BLANK);
  const [profile, setProfile] = useState(ACSA_DEFAULTS);
  const [rounds, setRounds] = useState([{ ...DEFAULT_ROUND }]);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  // Load matter in edit mode.
  useEffect(() => {
    if (!isEdit) return;
    api
      .get(`/matters/${matterId}`)
      .then((r) => {
        const m = r.data;
        setClientId(m.client_id);
        setIssuedCount(m.issued_invoice_count || 0);
        setForm({
          name: m.name || "",
          type: m.type || "ACSA_Client",
          hourly_rate: String(m.hourly_rate ?? 300),
          status: m.status || "Open",
          require_time_review: Boolean(m.require_time_review),
          open_date: m.open_date ? String(m.open_date).slice(0, 10) : "",
          close_date: m.close_date ? String(m.close_date).slice(0, 10) : "",
          notes: m.notes || "",
        });
        setProfile(profileFromMatter(m));
        setRounds(
          (m.coverage_rounds && m.coverage_rounds.length
            ? m.coverage_rounds
            : [{ ...DEFAULT_ROUND }]
          ).map((rr, i) => ({
            order: i + 1,
            l1_enabled: rr.l1_enabled,
            l2_enabled: rr.l2_enabled,
            l3_enabled: rr.l3_enabled,
          }))
        );
      })
      .catch((e) => setError(formatApiErrorDetail(e.response?.data?.detail) || e.message))
      .finally(() => setLoading(false));
  }, [isEdit, matterId]);

  // Warn on browser unload while dirty.
  useEffect(() => {
    const handler = (e) => {
      if (dirty) {
        e.preventDefault();
        e.returnValue = "";
      }
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [dirty]);

  const isAcsa = form.type === "ACSA_Client";

  const setField = (k, v) => {
    setForm((f) => ({ ...f, [k]: v }));
    setDirty(true);
  };
  const setProfileField = (k, v) => {
    setProfile((p) => ({ ...p, [k]: v }));
    setDirty(true);
  };
  const onTypeChange = (newType) => {
    setForm((f) => ({
      ...f,
      type: newType,
      hourly_rate: !isEdit ? (newType === "Client" ? "400" : "300") : f.hourly_rate,
    }));
    if (newType === "ACSA_Client" && rounds.length === 0) setRounds([{ ...DEFAULT_ROUND }]);
    setDirty(true);
  };

  const coverageErrors = useMemo(() => (isAcsa ? validateCoverage(profile) : {}), [isAcsa, profile]);
  const basicErrors = useMemo(() => {
    const e = {};
    if (!form.name.trim()) e.name = "Matter name is required";
    if (!(Number(form.hourly_rate) > 0)) e.hourly_rate = "Rate must be greater than 0";
    if (!(Number(profile.billing_increment_min) >= 1)) e.billing_increment_min = "Min 1 minute";
    return e;
  }, [form, profile.billing_increment_min]);

  const hasErrors = Object.keys(coverageErrors).length > 0 || Object.keys(basicErrors).length > 0;
  const previewValid = Object.keys(coverageErrors).length === 0 && Number(form.hourly_rate) > 0;

  const backTo = clientId ? `/clients-matters/${clientId}` : "/clients-matters";
  const cancel = () => {
    if (dirty && !window.confirm("Discard unsaved changes?")) return;
    navigate(backTo);
  };

  const save = async () => {
    if (hasErrors) return;
    setSaving(true);
    setError("");
    const base = {
      name: form.name.trim(),
      type: form.type,
      hourly_rate: Number(form.hourly_rate),
      billing_increment_min: Number(profile.billing_increment_min),
      status: form.status,
      require_time_review: Boolean(form.require_time_review),
      open_date: form.open_date || null,
      close_date: form.close_date || null,
      notes: form.notes || null,
    };
    if (isAcsa) {
      base.coverage_rounds = rounds.map((r, i) => ({
        order: i + 1,
        l1_enabled: r.l1_enabled,
        l2_enabled: r.l2_enabled,
        l3_enabled: r.l3_enabled,
      }));
      base.l1_free_hours = Number(profile.l1_free_hours);
      base.l1_percent = Number(profile.l1_percent);
      base.l2_percent = Number(profile.l2_percent);
      base.l2_cap = Number(profile.l2_cap);
      base.l3_percent = Number(profile.l3_percent);
      base.l3_cap = Number(profile.l3_cap);
    } else {
      base.coverage_rounds = [];
    }
    try {
      if (isEdit) {
        await api.patch(`/matters/${matterId}`, base);
      } else {
        await api.post("/matters", { ...base, client_id: clientId });
      }
      setDirty(false);
      navigate(backTo);
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="grid place-items-center py-24">
        <Loader2 className="h-6 w-6 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="matter-form-page">
      <button
        type="button"
        onClick={cancel}
        className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"
        data-testid="matter-back"
      >
        <ArrowLeft className="h-4 w-4" /> Back
      </button>

      <div>
        <h1 className="font-serif text-3xl font-semibold tracking-tight text-foreground">
          {isEdit ? "Edit matter" : "New matter"}
        </h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Set the rate and coverage rules. Use the live preview to sanity-check
          a rule set before saving.
        </p>
      </div>

      {isEdit && issuedCount > 0 && (
        <div
          data-testid="snapshot-locked-notice"
          className="flex items-start gap-2.5 rounded-md border border-status-sent/30 bg-status-sent/10 px-4 py-3 text-sm text-foreground"
        >
          <Lock className="mt-0.5 h-4 w-4 text-status-sent" />
          <span>
            This matter has {issuedCount} issued invoice{issuedCount > 1 ? "s" : ""}. Coverage
            changes apply to <strong>future</strong> invoices only — already-issued invoices
            keep their frozen snapshot.
          </span>
        </div>
      )}

      <div className="grid gap-6 lg:grid-cols-[1fr_360px]">
        {/* Left: form */}
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Matter details</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label htmlFor="m-name">Matter name</Label>
                <Input
                  id="m-name"
                  data-testid="matter-name-input"
                  value={form.name}
                  onChange={(e) => setField("name", e.target.value)}
                  placeholder="e.g. Custody Modification"
                />
                {basicErrors.name && <p className="mt-1 text-xs text-destructive">{basicErrors.name}</p>}
              </div>

              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <Label htmlFor="m-type">Type</Label>
                  <Select
                    id="m-type"
                    data-testid="matter-type-select"
                    value={form.type}
                    onChange={(e) => onTypeChange(e.target.value)}
                  >
                    <option value="ACSA_Client">{typeLabel("ACSA_Client")}</option>
                    <option value="Client">{typeLabel("Client")}</option>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="m-status">Status</Label>
                  <Select
                    id="m-status"
                    data-testid="matter-status-select"
                    value={form.status}
                    onChange={(e) => setField("status", e.target.value)}
                  >
                    <option value="Open">Open</option>
                    <option value="Closed">Closed</option>
                  </Select>
                </div>
              </div>

              <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                <div>
                  <Label htmlFor="m-rate">Hourly rate ($)</Label>
                  <Input
                    id="m-rate"
                    type="number"
                    step="any"
                    data-testid="matter-rate-input"
                    value={form.hourly_rate}
                    onChange={(e) => setField("hourly_rate", e.target.value)}
                  />
                  {basicErrors.hourly_rate && (
                    <p className="mt-1 text-xs text-destructive">{basicErrors.hourly_rate}</p>
                  )}
                </div>
                <div>
                  <Label htmlFor="m-inc">Increment (min)</Label>
                  <Input
                    id="m-inc"
                    type="number"
                    step="1"
                    data-testid="matter-increment-input"
                    value={profile.billing_increment_min}
                    onChange={(e) => setProfileField("billing_increment_min", e.target.value)}
                  />
                  {basicErrors.billing_increment_min && (
                    <p className="mt-1 text-xs text-destructive">{basicErrors.billing_increment_min}</p>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <Label htmlFor="m-open">Open date</Label>
                  <Input
                    id="m-open"
                    type="date"
                    data-testid="matter-open-date"
                    value={form.open_date}
                    onChange={(e) => setField("open_date", e.target.value)}
                  />
                </div>
                <div>
                  <Label htmlFor="m-close">Close date</Label>
                  <Input
                    id="m-close"
                    type="date"
                    data-testid="matter-close-date"
                    value={form.close_date}
                    onChange={(e) => setField("close_date", e.target.value)}
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="m-notes">Notes</Label>
                <Input
                  id="m-notes"
                  data-testid="matter-notes-input"
                  value={form.notes}
                  onChange={(e) => setField("notes", e.target.value)}
                  placeholder="optional"
                />
              </div>

              <div className="flex items-center justify-between rounded-md border border-border bg-surface px-3 py-2.5">
                <div className="pr-4">
                  <p className="text-sm font-medium text-foreground">Require time review</p>
                  <p className="text-xs text-muted-foreground">
                    New time entries land as <strong>Draft</strong> and must be approved before
                    they count toward billing.
                  </p>
                </div>
                <button
                  type="button"
                  role="switch"
                  aria-checked={form.require_time_review}
                  data-testid="matter-require-review-toggle"
                  onClick={() => setField("require_time_review", !form.require_time_review)}
                  className={cn(
                    "relative h-6 w-11 shrink-0 rounded-full transition-colors",
                    form.require_time_review ? "bg-primary" : "bg-input"
                  )}
                >
                  <span
                    className={cn(
                      "absolute top-0.5 h-5 w-5 rounded-full bg-white shadow-sm transition-transform",
                      form.require_time_review ? "translate-x-[22px]" : "translate-x-0.5"
                    )}
                  />
                </button>
              </div>
            </CardContent>
          </Card>

          {isAcsa ? (
            <Card>
              <CardHeader>
                <CardTitle>{abbr} coverage</CardTitle>
              </CardHeader>
              <CardContent>
                <CoverageEditor
                  profile={profile}
                  rounds={rounds}
                  errors={coverageErrors}
                  onProfileChange={setProfileField}
                  onRoundsChange={(r) => {
                    setRounds(r);
                    setDirty(true);
                  }}
                />
              </CardContent>
            </Card>
          ) : (
            <div className="flex items-center gap-2 rounded-md border border-border bg-secondary/50 px-4 py-3 text-sm text-muted-foreground">
              <Info className="h-4 w-4" /> Non-{abbr} matters have no coverage cascade — only
              the hourly rate applies.
            </div>
          )}

          {error && (
            <p
              data-testid="matter-form-error"
              className="rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive"
            >
              {error}
            </p>
          )}

          <div className="flex items-center justify-end gap-2">
            <Button variant="ghost" onClick={cancel} data-testid="matter-cancel">
              Cancel
            </Button>
            <Button onClick={save} disabled={saving || hasErrors} data-testid="matter-save">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
              {isEdit ? "Save changes" : "Create matter"}
            </Button>
          </div>
        </div>

        {/* Right: live preview */}
        <div>
          <Card className="lg:sticky lg:top-24">
            <CardHeader>
              <CardTitle>Live preview</CardTitle>
            </CardHeader>
            <CardContent>
              <CoveragePreview
                rate={form.hourly_rate}
                rounds={rounds}
                profile={profile}
                retainer={0}
                isAcsa={isAcsa}
                valid={previewValid}
              />
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
